import type {
  ComparisonRelation,
  ComparisonRecord,
  ComparisonDetail,
  ComparisonObservation,
} from "../../../shared/contracts/product-pages.js";

/** Display order only; canonical identities, filter keys and stored results stay unchanged. */
export function displaySides(
  relation: ComparisonRelation,
): ["left" | "right", "left" | "right"] {
  return relation.viewerSide === "right"
    ? ["right", "left"]
    : ["left", "right"];
}

export function displayRecord(
  relation: ComparisonRelation,
  record: ComparisonRecord = relation.record,
  includeTestHistory = record === relation.record,
) {
  return displaySides(relation).map((side) => ({
    name: relation[side].name,
    wins:
      record[`${side}Wins`] +
      (includeTestHistory
        ? (relation.testRounds?.filter((round) => round.winner === side)
            .length ?? 0)
        : 0),
  }));
}

export function phaseFinding(
  item: ComparisonObservation,
  relation: ComparisonRelation,
  label: string,
): string | null {
  if (!item.n) return null;
  const winner =
    item.leftWins > item.rightWins
      ? "left"
      : item.rightWins > item.leftWins
        ? "right"
        : null;
  if (!winner)
    return item.ties === item.n
      ? `${label}: tied in ${item.n} of ${item.n} recorded comparisons.`
      : `${label}: ${relation.left.name} and ${relation.right.name} each won ${item.leftWins} of ${item.n}.`;
  return `${label}: ${relation[winner].name} won ${item[`${winner}Wins`]} of ${item.n} recorded comparisons.`;
}

export function sharedFinding(
  detail: ComparisonDetail,
): { text: string; basis: string } | null {
  const { front9, back9, opening } = detail.phases;
  const basis =
    detail.filters.basis === "net" ? "Net strokes" : "Gross strokes";
  const frontWinner =
    front9.leftWins > front9.rightWins
      ? "left"
      : front9.rightWins > front9.leftWins
        ? "right"
        : null;
  const backWinner =
    back9.leftWins > back9.rightWins
      ? "left"
      : back9.rightWins > back9.leftWins
        ? "right"
        : null;
  if (
    front9.n &&
    back9.n &&
    frontWinner &&
    backWinner &&
    frontWinner !== backWinner
  )
    return {
      text: `${detail.relation[frontWinner].name} won the first nine ${front9[`${frontWinner}Wins`]}/${front9.n} times. ${detail.relation[backWinner].name} won the last nine ${back9[`${backWinner}Wins`]}/${back9.n} times.`,
      basis: `${basis} · recorded rounds, not a prediction`,
    };
  const text = phaseFinding(opening, detail.relation, "The opening hole");
  return text
    ? {
        text,
        basis: `${basis} · ${opening.sampleRounds} shared rounds · recorded history`,
      }
    : null;
}
